import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAxiosInstance } from '@gov-nx/api/axios';
import { isResponseStatus } from '@gov-nx/api/errors/utils';
import type { DatoveSchrankyDatovaSchrankaStavDto } from '@gov-nx/api/generated/datove-schranky.types';
import { usePoQuery } from '@gov-nx/api/hooks/usePoQuery';
import { fetchDataBoxesUnreadMessagesCountsQuery } from '@gov-nx/api/queries/dataBoxQuery';
import { useAppContext } from '@gov-nx/core/app/context/AppContext';
import { getKeys, getValues } from '@gov-nx/core/types/helpers.types';

const databoxUnreadCounts = (
	databoxes: DatoveSchrankyDatovaSchrankaStavDto[]
) =>
	databoxes.reduce<Record<string, number>>((acc, databox) => {
		if (databox.datovaSchrankaId && databox.pocetNeprecteno !== undefined) {
			return {
				...acc,
				...{ [databox.datovaSchrankaId]: databox.pocetNeprecteno },
			};
		}

		return acc;
	}, {});

const queryKey = ['data-boxes-unread-messages-counts'];

export const useDataBoxUnreadMessages = () => {
	const { setAppError } = useAppContext();
	const { axiosInstance } = useAxiosInstance();
	const queryClient = useQueryClient();
	const query = usePoQuery(
		{
			queryKey,
			queryFn: () => fetchDataBoxesUnreadMessagesCountsQuery(axiosInstance),
			onError: setAppError,
			enabled: false,
			retry: 1,
		},
		{
			errorIgnoreFilter: isResponseStatus(404),
		}
	);

	const dataBoxesUnreadMessagesCounts = databoxUnreadCounts(
		query.data?.data.seznam ?? []
	);

	const unreadMessagesCount = getValues(dataBoxesUnreadMessagesCounts).reduce(
		(acc, item) => acc + item,
		0
	);

	const initDataBoxUnreadMessages = useCallback(async (): Promise<void> => {
		await query.refetch();
	}, [query]);

	const refetchDataBoxUnreadMessages = useCallback(async (): Promise<void> => {
		await queryClient.invalidateQueries({
			queryKey,
		});

		await query.refetch();
	}, [query, queryClient]);

	return {
		initDataBoxUnreadMessages,
		refetchDataBoxUnreadMessages,
		isRefetching: query.isFetching,
		isLoading: query.isLoading,
		unreadMessagesCount,
		getUnreadMessagesCounts: (dataBoxId: string) =>
			dataBoxesUnreadMessagesCounts[dataBoxId] ?? null,
		hasUnreadMessages: unreadMessagesCount > 0,
		unreadMessagesDataboxIds: getKeys(dataBoxesUnreadMessagesCounts),
	};
};
